import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme.js';
import { Button } from '../../ui/Button/index.js';
import { LanguageMenu } from '../LanguageMenu/index.js';

interface HeaderProps {
  /** ユーザー情報 */
  user?: {
    name: string;
    email: string;
  } | null;
  /** ログアウト処理 */
  onLogout?: () => void;
  /** サイドバー開閉（モバイル用） */
  onMenuToggle?: () => void;
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business 固定ヘッダー。ガラスモーフィズム背景にグラデーションロゴ・テーマ切替・言語切替・ユーザーアバター/メニュー（ログアウト）を配置。モバイルハンバーガー対応
 * @graph-connects useTheme [calls] テーマ切替フックでダーク/ライトモード管理
 * @graph-connects LanguageMenu [calls] ja / en / vi の言語切替メニュー
 */
export function Header({ user, onLogout, onMenuToggle }: HeaderProps) {
  const { t } = useTranslation();
  const { resolved, setTheme } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const toggleTheme = () => {
    setTheme(resolved === 'dark' ? 'light' : 'dark');
  };

  return (
    <header className="sticky top-0 z-[var(--z-sticky)] bg-[var(--color-bg-surface)]/80 backdrop-blur-xl border-b border-[var(--color-border)]">
      <div className="flex items-center justify-between h-14 px-4 md:px-6">
        {/* 左: メニューボタン（モバイル） + ロゴ */}
        <div className="flex items-center gap-3">
          {/* モバイルメニューボタン */}
          <button
            onClick={onMenuToggle}
            className="md:hidden p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
            aria-label={t('app.openMenu')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          {/* ロゴ */}
          <div className="flex items-center gap-2">
            <svg
              className="w-8 h-8 glow-accent"
              viewBox="0 0 512 512"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="hdr-bg" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
              </defs>
              <rect width="512" height="512" rx="108" fill="url(#hdr-bg)" />
              <circle cx="256" cy="256" r="28" fill="white" opacity="0.95" />
              <circle cx="160" cy="148" r="18" fill="white" opacity="0.85" />
              <circle cx="352" cy="148" r="18" fill="white" opacity="0.85" />
              <circle cx="120" cy="280" r="18" fill="white" opacity="0.85" />
              <circle cx="392" cy="280" r="18" fill="white" opacity="0.85" />
              <circle cx="180" cy="388" r="18" fill="white" opacity="0.85" />
              <circle cx="332" cy="388" r="18" fill="white" opacity="0.85" />
              <g stroke="white" strokeWidth="2.5" strokeLinecap="round" opacity="0.4">
                <line x1="256" y1="256" x2="160" y2="148" />
                <line x1="256" y1="256" x2="352" y2="148" />
                <line x1="256" y1="256" x2="120" y2="280" />
                <line x1="256" y1="256" x2="392" y2="280" />
                <line x1="256" y1="256" x2="180" y2="388" />
                <line x1="256" y1="256" x2="332" y2="388" />
              </g>
              <g stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.2">
                <line x1="160" y1="148" x2="352" y2="148" />
                <line x1="160" y1="148" x2="120" y2="280" />
                <line x1="352" y1="148" x2="392" y2="280" />
                <line x1="120" y1="280" x2="180" y2="388" />
                <line x1="392" y1="280" x2="332" y2="388" />
                <line x1="180" y1="388" x2="332" y2="388" />
              </g>
            </svg>
            <span className="hidden sm:block font-semibold gradient-text text-lg">
              {t('app.name')}
            </span>
          </div>
        </div>

        {/* 右: アクション */}
        <div className="flex items-center gap-2">
          {/* テーマ切替 */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
            aria-label={t('settings.theme')}
          >
            {resolved === 'dark' ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            )}
          </button>

          {/* 言語切替 */}
          <LanguageMenu />

          {/* ユーザーメニュー */}
          {user ? (
            <div className="relative">
              <button
                onClick={() => {
                  setIsUserMenuOpen(!isUserMenuOpen);
                }}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
                aria-label={t('app.userMenu')}
              >
                {/* アバター（イニシャル） */}
                <div className="w-7 h-7 rounded-full bg-[var(--color-accent-muted)] flex items-center justify-center">
                  <span className="text-xs font-semibold text-[var(--color-accent-primary)]">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="hidden sm:block text-sm text-[var(--color-text-primary)]">
                  {user.name}
                </span>
                <svg
                  className={`w-4 h-4 text-[var(--color-text-muted)] transition-transform ${
                    isUserMenuOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* ドロップダウンメニュー */}
              {isUserMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                    }}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 mt-2 w-56 z-20 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl shadow-xl py-1 animate-fade-in">
                    <div className="px-4 py-3 border-b border-[var(--color-border)]">
                      <p className="text-sm font-medium text-[var(--color-text-primary)]">
                        {user.name}
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)] truncate">
                        {user.email}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        onLogout?.();
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-error)] hover:bg-[var(--color-bg-hover)] transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      {t('auth.logout')}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Button size="sm">{t('auth.login')}</Button>
          )}
        </div>
      </div>
    </header>
  );
}
